import React, { useEffect, useState } from 'react';
import { Download, RefreshCw, Trash2 } from 'lucide-react';
import api from '../lib/api';
import HelpCallout from './HelpCallout';

export default function AdminSubscribers() {
  const [items, setItems] = useState([]);
  const [eoConfigured, setEoConfigured] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const load = () => api.get('/admin/subscribers').then((r) => setItems(r.data.items));
  useEffect(() => {
    load();
    api.get('/admin/stats').then((r) => setEoConfigured(!!r.data?.emailoctopus_configured));
  }, []);

  async function removeSub(s) {
    if (!window.confirm(`Delete ${s.email} from the subscriber list?\n\nThis removes them here AND from your EmailOctopus list (GDPR erasure). It cannot be undone.`)) return;
    try { await api.delete(`/admin/subscribers/${s.id}`); load(); }
    catch { alert('Delete failed — try again'); }
  }

  async function syncUnsubscribes() {
    setSyncing(true); setSyncMsg('');
    try {
      const r = await api.post('/admin/subscribers/sync-unsubscribes');
      setSyncMsg(`Checked EmailOctopus: ${r.data.unsubscribed_on_emailoctopus} unsubscribed/bounced there, ${r.data.removed_locally} removed from this list.`);
      load();
    } catch (e) {
      setSyncMsg(e.response?.data?.detail || 'Sync failed — try again');
    } finally { setSyncing(false); }
  }

  function download() {
    const csv = ['email,name,source,created_at', ...items.map((s) => `${s.email},${s.name || ''},${s.source || ''},${s.created_at}`)].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'goleicestershire-subscribers.csv';
    a.click();
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="label text-heritage mb-2">Newsletter</div>
          <h1 className="font-display text-4xl">Subscribers ({items.length})</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={syncUnsubscribes} disabled={syncing || eoConfigured === false} data-testid="subs-sync-unsubscribes"
            className="border border-charcoal/20 px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            <RefreshCw size={14} className={syncing ? 'animate-spin' : ''}/> {syncing ? 'Syncing…' : 'Sync unsubscribes'}
          </button>
          <button onClick={download} data-testid="subs-csv" className="bg-charcoal text-base px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2">
            <Download size={14}/> Export CSV
          </button>
        </div>
      </div>
      {syncMsg && <div data-testid="subs-sync-result" className="mb-6 bg-forest/10 border border-forest/30 rounded-2xl p-4 text-sm text-charcoal">{syncMsg}</div>}

      <HelpCallout
        title="Everyone who's signed up for the weekend digest by email."
        description="Captured automatically when visitors submit any newsletter signup form on the public site. Each new signup is also pushed straight into your EmailOctopus list (when configured), so they receive the Friday newsletter without any manual import. You can still export to CSV at any time as a backup."
        appearsAt={[
          { label: 'Homepage newsletter section', where: 'final dark CTA "Get the weekend, served warm"' },
          { label: 'Homepage AI Weekend Digest block', where: '"Get it Friday morning" signup' },
          { label: '/digest page', where: 'subscribe form near the top' },
          { label: 'EmailOctopus list', where: 'each signup is added as a subscribed contact, tagged with its signup source (homepage / digest / footer)' },
        ]}
      />

      {eoConfigured === false && (
        <div data-testid="eo-not-configured" className="mb-8 bg-mustard/20 border border-mustard rounded-2xl p-5 text-sm text-charcoal">
          <strong>EmailOctopus not connected.</strong> Signups are saved here but <em>not</em> pushed to your mailing list yet.
          Add <code className="bg-white px-1.5 py-0.5 rounded">EMAILOCTOPUS_API_KEY</code> and <code className="bg-white px-1.5 py-0.5 rounded">EMAILOCTOPUS_LIST_ID</code> in the backend <code className="bg-white px-1.5 py-0.5 rounded">.env</code> to enable automatic sync.
        </div>
      )}
      {eoConfigured === true && (
        <div data-testid="eo-configured" className="mb-8 bg-forest/10 border border-forest/30 rounded-2xl p-5 text-sm text-charcoal">
          <strong className="text-forest">EmailOctopus connected.</strong> Every new signup is automatically added to your list and tagged with its source.
        </div>
      )}

      <div className="bg-white border border-charcoal/10 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-admin text-charcoal/60 text-xs uppercase tracking-wider">
            <tr><th className="text-left p-4">Email</th><th className="text-left p-4">Source</th><th className="text-left p-4">Joined</th><th className="p-4"></th></tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-t border-charcoal/5">
                <td className="p-4 font-semibold">{s.email}</td>
                <td className="p-4 text-muted">{s.source || '—'}</td>
                <td className="p-4 text-muted">{new Date(s.created_at).toLocaleDateString('en-GB')}</td>
                <td className="p-4 text-right">
                  <button onClick={() => removeSub(s)} data-testid={`subs-delete-${s.id}`} title="Delete subscriber (GDPR erasure — also removes from EmailOctopus)"
                    className="p-1.5 rounded-lg hover:bg-stone text-heritage"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="p-16 text-center text-muted">No subscribers yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
